;(() => {
  // Every campaign section includes this asset so sections also work on their own.
  // Keep one runtime, including when the theme editor executes an asset again.
  if (window.xmasCraftBoxesRuntime) {
    window.xmasCraftBoxesRuntime.scan(document)
    return
  }

  const instances = new Map()
  const dependencies = new Map()
  const buttonLabels = new WeakMap()
  let pendingAdd = null

  const loadDependency = (name, url) => {
    if (window[name]) return Promise.resolve(window[name])
    if (dependencies.has(name)) return dependencies.get(name)
    const promise = new Promise((resolve, reject) => {
      let script = document.querySelector(`script[src="${url}"]`)
      const created = !script
      if (created) {
        script = document.createElement('script')
        script.src = url
        script.async = true
      }
      const finish = () => {
        script.removeEventListener('load', onLoad)
        script.removeEventListener('error', onError)
      }
      const onLoad = () => {
        finish()
        if (window[name]) resolve(window[name])
        else reject(new Error(`${name} did not load`))
      }
      const onError = () => {
        finish()
        if (created) script.remove()
        reject(new Error(`${name} could not load`))
      }
      script.addEventListener('load', onLoad)
      script.addEventListener('error', onError)
      if (created) document.head.appendChild(script)
      if (window[name]) onLoad()
    })
    dependencies.set(name, promise)
    promise.catch(() => dependencies.delete(name))
    return promise
  }

  const loadEmbla = () =>
    loadDependency('EmblaCarousel', 'https://unpkg.com/embla-carousel@8.6.0/embla-carousel.umd.js')

  const hero = () => [...instances.values()].find((instance) => instance.type === 'hero' && instance.root.isConnected)
  const cardFor = (instance, number) =>
    instance
      ?.cards()
      .find((card) => (number == null ? card.classList.contains('is-selected') : card.dataset.box === String(number)))

  const statusFor = (button, root) =>
    button?.parentElement?.querySelector('.xb-cart-status') || root?.querySelector('.xb-cart-status')

  const setStatus = (button, root, message) => {
    const status = statusFor(button, root)
    if (!status) return
    status.textContent = message
    status.hidden = !message
  }

  const syncButton = (button, card) => {
    if (!button) return
    if (!buttonLabels.has(button)) buttonLabels.set(button, button.dataset.label || button.textContent.trim())
    const soldOut = card?.dataset.available === 'soldout'
    button.disabled = !!pendingAdd || !!soldOut
    button.textContent = pendingAdd?.button === button ? 'Adding…' : soldOut ? 'Sold Out' : buttonLabels.get(button)
    button.setAttribute('aria-busy', pendingAdd?.button === button ? 'true' : 'false')
  }

  const syncPurchaseButtons = () => {
    const currentHero = hero()
    for (const instance of instances.values()) {
      if (instance.type === 'hero') syncButton(instance.root.querySelector('.xb-atc'), cardFor(instance))
      if (instance.type === 'projects') {
        instance.root
          .querySelectorAll('.xb-add-box')
          .forEach((button) => syncButton(button, cardFor(currentHero, button.dataset.box)))
      }
    }
  }

  const addBox = async (number, button) => {
    if (pendingAdd) return
    // Resolve the current hero each time; never retain product data from an old section.
    const currentHero = hero()
    const card = cardFor(currentHero, number)
    const root = currentHero?.root
    const variantId = Number(card?.dataset.variantId)
    setStatus(button, root, '')
    if (!variantId || card.dataset.available === 'false') {
      setStatus(button, root, 'This box is not available yet. Please check back soon!')
      return
    }
    if (card.dataset.available === 'soldout') {
      setStatus(button, root, 'This box is sold out. Please choose another box.')
      syncPurchaseButtons()
      return
    }

    pendingAdd = { button }
    syncPurchaseButtons()
    try {
      // Match single-item product forms so the cart app can handle the AJAX add.
      const formData = new FormData()
      formData.append('id', String(variantId))
      formData.append('quantity', '1')
      const response = await fetch(currentHero.config.cartAddUrl, {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
        body: formData,
      })
      if (!response.ok) {
        const error = await response.json().catch(() => ({}))
        throw new Error(error.description || 'Could not add this box to your cart. Please try again.')
      }
      setStatus(button, root, 'Added to your cart.')
      document.dispatchEvent(
        new CustomEvent('xmas-craft-boxes:added', { detail: { box: Number(card.dataset.box), variantId } }),
      )
    } catch (error) {
      setStatus(
        button,
        root,
        error.name === 'TypeError'
          ? 'Could not connect to your cart. Please try again.'
          : error.message || 'Could not add this box to your cart. Please try again.',
      )
    } finally {
      pendingAdd = null
      syncPurchaseButtons()
    }
  }

  const createInstance = (root, type, config) => {
    const cleanups = []
    const instance = {
      root,
      type,
      config,
      destroyed: false,
      listen(node, name, handler) {
        if (!node) return
        node.addEventListener(name, handler)
        cleanups.push(() => node.removeEventListener(name, handler))
      },
      destroy() {
        instance.destroyed = true
        cleanups.forEach((cleanup) => cleanup())
        instance.embla?.destroy()
      },
    }
    return instance
  }

  const initHero = (instance) => {
    const { root } = instance
    instance.cards = () => Array.from(root.querySelectorAll('.xb-card'))
    const atc = root.querySelector('.xb-atc')
    const viewport = root.querySelector('.xb-embla')
    const dotsNode = root.querySelector('.xb-dots')
    const thumbs = Array.from(root.querySelectorAll('.xb-thumb'))
    const thumbRow = root.querySelector('.xb-thumbs')
    let requestedSlide = null

    instance.select = (card) => {
      instance.cards().forEach((other) => {
        const selected = other === card
        other.classList.toggle('is-selected', selected)
        other.classList.toggle('is-open', selected)
        other.setAttribute('aria-checked', String(selected))
        other.querySelector('.xb-toggle')?.setAttribute('aria-expanded', String(selected))
      })
      const slide = parseInt(card.dataset.slide, 10)
      if (slide > 0) {
        requestedSlide = slide - 1
        instance.embla?.scrollTo(requestedSlide)
      }
      setStatus(atc, root, '')
      syncPurchaseButtons()
    }

    instance.cards().forEach((card) => {
      instance.listen(card, 'click', (event) => {
        const toggle = event.target.closest('.xb-toggle')
        if (toggle && card.classList.contains('is-selected')) {
          toggle.setAttribute('aria-expanded', String(card.classList.toggle('is-open')))
          return
        }
        if (!card.classList.contains('is-selected')) instance.select(card)
      })
      instance.listen(card, 'keydown', (event) => {
        if (event.target !== card) return
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          instance.select(card)
        }
      })
    })
    instance.listen(atc, 'click', () => addBox(null, atc))

    if (!viewport) return
    loadEmbla()
      .then((EmblaCarousel) => {
        if (instance.destroyed || !root.isConnected) return
        const embla = EmblaCarousel(viewport, { align: 'start', containScroll: 'trimSnaps' })
        instance.embla = embla
        const renderDots = () => {
          if (!dotsNode) return
          dotsNode.innerHTML = embla
            .scrollSnapList()
            .map(
              (_, index) =>
                `<button type="button" class="xb-dot" aria-label="Go to slide ${index + 1}" data-index="${index}"></button>`,
            )
            .join('')
        }
        const syncUi = () => {
          const selected = embla.selectedScrollSnap()
          dotsNode?.querySelectorAll('.xb-dot').forEach((dot, index) => {
            dot.classList.toggle('is-selected', index === selected)
            dot.setAttribute('aria-pressed', String(index === selected))
          })
          thumbs.forEach((thumb) => {
            const active = Number(thumb.dataset.index) === selected
            thumb.classList.toggle('is-selected', active)
            thumb.setAttribute('aria-pressed', String(active))
          })
          const active = thumbs.find((thumb) => Number(thumb.dataset.index) === selected)
          if (active && thumbRow?.offsetParent) {
            const left = active.offsetLeft - thumbRow.offsetLeft
            if (left < thumbRow.scrollLeft || left + active.offsetWidth > thumbRow.scrollLeft + thumbRow.clientWidth) {
              thumbRow.scrollTo({ left: left - (thumbRow.clientWidth - active.offsetWidth) / 2, behavior: 'smooth' })
            }
          }
        }
        renderDots()
        syncUi()
        embla.on('select', syncUi)
        embla.on('reInit', () => {
          renderDots()
          syncUi()
        })
        // Delegate dots so reInit does not accumulate listeners on replaced dots.
        instance.listen(dotsNode, 'click', (event) => {
          const dot = event.target.closest('.xb-dot')
          if (dot) embla.scrollTo(Number(dot.dataset.index))
        })
        instance.listen(root.querySelector('.xb-prev'), 'click', () => embla.scrollPrev())
        instance.listen(root.querySelector('.xb-next'), 'click', () => embla.scrollNext())
        thumbs.forEach((thumb) => instance.listen(thumb, 'click', () => embla.scrollTo(Number(thumb.dataset.index))))
        if (requestedSlide !== null) embla.scrollTo(requestedSlide)
      })
      .catch(() => {
        if (instance.destroyed) return
        // Keep photos accessible even if the external carousel script fails.
        viewport.style.overflowX = 'auto'
        root.querySelectorAll('.xb-prev, .xb-next').forEach((button) => {
          button.style.setProperty('display', 'none', 'important')
        })
      })
  }

  const initProjects = (instance) => {
    instance.root.querySelectorAll('.xb-add-box').forEach((button) => {
      instance.listen(button, 'click', () => addBox(button.dataset.box, button))
    })
  }

  const initFaq = (instance) => {
    const items = instance.root.querySelectorAll('.xb-faq-item')
    items.forEach((item) => {
      instance.listen(item.querySelector('.xb-faq-question'), 'click', () => {
        const open = !item.classList.contains('is-open')
        items.forEach((other) => {
          const active = other === item && open
          other.classList.toggle('is-open', active)
          other.querySelector('.xb-faq-question').setAttribute('aria-expanded', String(active))
        })
      })
    })
  }

  const initTestimonials = (instance) => {
    const viewport = instance.root.querySelector('.embla__viewport')
    if (!viewport) return
    Promise.all([
      loadEmbla(),
      loadDependency(
        'EmblaCarouselAutoScroll',
        'https://unpkg.com/embla-carousel-auto-scroll@8.6.0/embla-carousel-auto-scroll.umd.js',
      ),
    ])
      .then(([EmblaCarousel, EmblaCarouselAutoScroll]) => {
        if (instance.destroyed || !instance.root.isConnected) return
        instance.embla = EmblaCarousel(viewport, { loop: true }, [
          EmblaCarouselAutoScroll({ stopOnInteraction: false, speed: 1 }),
        ])
      })
      .catch(() => {
        if (!instance.destroyed) viewport.style.overflowX = 'auto'
      })
  }

  const initializers = { hero: initHero, projects: initProjects, faq: initFaq, testimonials: initTestimonials }
  const destroyWithin = (scope) => {
    for (const [root, instance] of instances) {
      if (scope === root || scope.contains(root) || !root.isConnected) {
        instance.destroy()
        instances.delete(root)
      }
    }
  }

  const scan = (scope) => {
    // Section replacement can precede the editor event; remove detached instances first.
    for (const [root, instance] of instances) {
      if (!root.isConnected) {
        instance.destroy()
        instances.delete(root)
      }
    }
    scope.querySelectorAll('script[data-xmas-boxes]').forEach((marker) => {
      const root = marker.closest('[id^="shopify-section-"]')
      const type = marker.dataset.xmasBoxes
      if (!root || instances.has(root) || !initializers[type]) return
      let config
      try {
        config = JSON.parse(marker.textContent)
      } catch (error) {
        return
      }
      const instance = createInstance(root, type, config)
      instances.set(root, instance)
      initializers[type](instance)
    })
    syncPurchaseButtons()
  }

  window.xmasCraftBoxesRuntime = { scan }
  window.xmasCraftBoxes = {
    add: addBox,
    select(number) {
      const currentHero = hero()
      const card = cardFor(currentHero, number)
      if (card) currentHero.select(card)
    },
  }
  document.addEventListener('shopify:section:load', (event) => {
    destroyWithin(event.target)
    scan(event.target)
  })
  document.addEventListener('shopify:section:unload', (event) => {
    destroyWithin(event.target)
    syncPurchaseButtons()
  })
  if (document.readyState === 'loading')
    document.addEventListener('DOMContentLoaded', () => scan(document), { once: true })
  else scan(document)
})()
